import { useState, useEffect } from "react";
import { 
  Search, 
  Filter, 
  List,
  Calendar as CalendarIcon, 
  ExternalLink, 
  Bell, 
  Clock,
  CalendarDays,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Check,
  Settings2,
  Sparkles,
  Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../lib/utils";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { format, parseISO, isPast, isToday, isFuture, addDays, startOfWeek, endOfWeek, isWithinInterval } from "date-fns";

// Default municipality colors (Tailwind classes)
const DEFAULT_MUNICIPALITY_COLORS: Record<string, string> = {
  'mississauga': 'bg-emerald-100 text-emerald-800',
  'brampton': 'bg-blue-100 text-blue-800',
  'durham_region': 'bg-purple-100 text-purple-800',
  'durham region': 'bg-purple-100 text-purple-800',
  'oakville': 'bg-orange-100 text-orange-800',
  'oshawa': 'bg-pink-100 text-pink-800',
  'peel_region': 'bg-indigo-100 text-indigo-800',
  'peel region': 'bg-indigo-100 text-indigo-800',
  'vaughan': 'bg-teal-100 text-teal-800',
  'york_region': 'bg-amber-100 text-amber-800',
  'york region': 'bg-amber-100 text-amber-800',
  'toronto': 'bg-red-100 text-red-800',
  'markham': 'bg-lime-100 text-lime-800',
  'ottawa': 'bg-cyan-100 text-cyan-800',
  'richmond_hill': 'bg-rose-100 text-rose-800',
  'richmond hill': 'bg-rose-100 text-rose-800',
};

// Auto-assign colors for municipalities not in the default list
const getAutoAssignedColor = (municipality: string): string => {
  // Normalize municipality name for lookup
  const normalized = municipality.toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/region/g, 'region')
    .replace(/city of /g, '')
    .replace(/town of /g, '');
  
  // Check if we have a default color
  if (DEFAULT_MUNICIPALITY_COLORS[normalized]) {
    return DEFAULT_MUNICIPALITY_COLORS[normalized];
  }
  
  // If not, check the original name format
  if (DEFAULT_MUNICIPALITY_COLORS[municipality.toLowerCase()]) {
    return DEFAULT_MUNICIPALITY_COLORS[municipality.toLowerCase()];
  }
  
  // Auto-assign from a predefined list of colors
  const autoColors = [
    'bg-emerald-100 text-emerald-800',
    'bg-blue-100 text-blue-800',
    'bg-purple-100 text-purple-800',
    'bg-orange-100 text-orange-800',
    'bg-pink-100 text-pink-800',
    'bg-indigo-100 text-indigo-800',
    'bg-teal-100 text-teal-800',
    'bg-amber-100 text-amber-800',
    'bg-red-100 text-red-800',
    'bg-lime-100 text-lime-800',
    'bg-cyan-100 text-cyan-800',
    'bg-rose-100 text-rose-800',
    'bg-violet-100 text-violet-800',
    'bg-sky-100 text-sky-800',
  ];
  
  // Create a simple hash from the string to get consistent color
  let hash = 0;
  for (let i = 0; i < normalized.length; i++) {
    hash = normalized.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colorIndex = Math.abs(hash) % autoColors.length;
  return autoColors[colorIndex];
};

// Color options for meeting types
const COLOR_OPTIONS = [
  { name: "Blue", bg: "bg-blue-100", text: "text-blue-700" },
  { name: "Red", bg: "bg-red-100", text: "text-red-700" },
  { name: "Green", bg: "bg-green-100", text: "text-green-700" },
  { name: "Yellow", bg: "bg-yellow-100", text: "text-yellow-700" },
  { name: "Purple", bg: "bg-purple-100", text: "text-purple-700" },
  { name: "Pink", bg: "bg-pink-100", text: "text-pink-700" },
  { name: "Orange", bg: "bg-orange-100", text: "text-orange-700" },
  { name: "Indigo", bg: "bg-indigo-100", text: "text-indigo-700" },
  { name: "Cyan", bg: "bg-cyan-100", text: "text-cyan-700" },
  { name: "Violet", bg: "bg-violet-100", text: "text-violet-700" },
  { name: "Rose", bg: "bg-rose-100", text: "text-rose-700" },
  { name: "Sky", bg: "bg-sky-100", text: "text-sky-700" },
];

export default function Meetings() {
  const [viewMode, setViewMode] = useState<"list" | "calendar">("list");
  const [timeFilter, setTimeFilter] = useState<"upcoming" | "past">("upcoming");
  const [notifiedMeetings, setNotifiedMeetings] = useState<number[]>([]);
  const [currentCalendarDate, setCurrentCalendarDate] = useState<Date>(new Date());
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedMunicipalities, setSelectedMunicipalities] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [displayLimit, setDisplayLimit] = useState<number>(20); // Initial limit for future meetings
  
  // Modal State
  const [selectedDate, setSelectedDate] = useState<any>(null);
  const [viewSummaryMeeting, setViewSummaryMeeting] = useState<any>(null);
  const [colorCustomizationOpen, setColorCustomizationOpen] = useState(false);
  
  // Color mapping state - maps municipality to color
  const [municipalityColors, setMunicipalityColors] = useState<Record<string, string>>(() => {
    // Load from localStorage if available
    const saved = localStorage.getItem('municipalityColors');
    if (saved) {
      return JSON.parse(saved);
    }
    // Return empty object - defaults will be applied when meetings are loaded
    return {};
  });
  
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch meetings
  const { data: meetingsData, isLoading } = useQuery({
    queryKey: ['meetings'],
    queryFn: () => apiClient.meetings.list(),
  });


  // Track meeting mutation
  const trackMeetingMutation = useMutation({
    mutationFn: (meetingId: string) => apiClient.meetings.track(meetingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      toast({
        title: "Meeting tracked",
        description: "You'll be notified of updates",
      });
    },
  });

  const handleNotify = (id: number) => {
    trackMeetingMutation.mutate(String(id));
    setNotifiedMeetings(prev => {
      if (prev.includes(id)) return prev.filter(mId => mId !== id);
      return [...prev, id];
    });
  };

  // Get unique municipalities for color customization
  const uniqueMunicipalitiesForColors: string[] = Array.from(new Set(
    (meetingsData || []).map((m: any) => m.municipality || m.municipality_name).filter(Boolean)
  )) as string[];

  // Transform meetings data to expected format
  const allMeetings = meetingsData ? meetingsData.map((meeting: any) => {
    const dateStr = meeting.date || meeting.event_start || meeting.start_at_local || meeting.meeting_date;
    let meetingDate: Date | null = null;
    let formattedDate = "";
    let formattedTime = "";
    let dayName = "";
    let dayNumber = "";
    let status = "no-agenda";
    
    if (dateStr) {
      try {
        meetingDate = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
        if (!isNaN(meetingDate.getTime())) {
          formattedDate = format(meetingDate, 'MMM d, yyyy');
          formattedTime = format(meetingDate, 'h:mm a');
          dayName = format(meetingDate, 'EEE');
          dayNumber = format(meetingDate, 'd');
          
          // Determine status based on date and agenda
          if (isPast(meetingDate) && !isToday(meetingDate)) {
            status = meeting.has_agenda || meeting.hasAgenda ? "past" : "no-agenda";
          } else if (isToday(meetingDate)) {
            status = meeting.has_agenda || meeting.hasAgenda ? "today" : "no-agenda";
          } else {
            status = meeting.has_agenda || meeting.hasAgenda ? "posted" : "no-agenda";
          }
        }
          } catch (e) {
        console.error('Error parsing date:', dateStr, e);
      }
    }
    
    return {
      id: meeting.id,
      title: meeting.title || meeting.meeting_name || meeting.meeting_title,
      date: dateStr,
      dateObj: meetingDate,
      formattedDate,
      formattedTime: formattedTime || "N/A",
      dayName,
      dayNumber,
      time: formattedTime || meeting.time || meeting.meeting_time || "N/A",
      municipality: meeting.municipality || meeting.municipality_name,
      status,
      hasAgenda: meeting.has_agenda || meeting.hasAgenda || false,
      items: meeting.items || meeting.agenda_item_count || 0,
      type: meeting.type || meeting.committee || "meeting",
      summary: meeting.summary || meeting.description,
      alertCount: meeting.alert_count || meeting.alertCount || 0,
      hasAlert: meeting.has_alert || meeting.hasAlert || false,
      documentUrl: meeting.document_url,
      // Get color for this municipality - use saved color, default color, or auto-assign
      color: municipalityColors[meeting.municipality || meeting.municipality_name] || 
             getAutoAssignedColor(meeting.municipality || meeting.municipality_name || '')
    };
  }).sort((a: any, b: any) => {
    // Sort by date, upcoming first
    if (!a.dateObj && !b.dateObj) return 0;
    if (!a.dateObj) return 1;
    if (!b.dateObj) return -1;
    return a.dateObj.getTime() - b.dateObj.getTime();
  }) : [];
  
  // Get unique municipalities for filtering
  const uniqueMunicipalities = Array.from(new Set(
    allMeetings.map((m: any) => m.municipality).filter(Boolean)
  )) as string[];

  // Initialize default colors for municipalities that don't have saved colors
  useEffect(() => {
    if (uniqueMunicipalities.length > 0 && Object.keys(municipalityColors).length === 0) {
      const defaultColors: Record<string, string> = {};
      uniqueMunicipalities.forEach((municipality: string) => {
        defaultColors[municipality] = getAutoAssignedColor(municipality);
      });
      setMunicipalityColors(defaultColors);
      localStorage.setItem('municipalityColors', JSON.stringify(defaultColors));
    } else if (uniqueMunicipalities.length > 0) {
      // Ensure all municipalities have colors (in case new ones were added)
      const updatedColors = { ...municipalityColors };
      let hasUpdates = false;
      uniqueMunicipalities.forEach((municipality: string) => {
        if (!updatedColors[municipality]) {
          updatedColors[municipality] = getAutoAssignedColor(municipality);
          hasUpdates = true;
        }
      });
      if (hasUpdates) {
        setMunicipalityColors(updatedColors);
        localStorage.setItem('municipalityColors', JSON.stringify(updatedColors));
      }
    }
  }, [uniqueMunicipalities, municipalityColors]);

  // Filter meetings by selected municipalities and search query (for calendar view)
  const municipalityFilteredMeetings = allMeetings.filter((meeting: any) => {
    // Municipality filter
    if (selectedMunicipalities.length > 0 && !selectedMunicipalities.includes(meeting.municipality)) {
      return false;
    }
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const title = (meeting.title || "").toLowerCase();
      const municipality = (meeting.municipality || "").toLowerCase();
      const type = (meeting.type || "").toLowerCase();
      const summary = (meeting.summary || "").toLowerCase();
      
      return title.includes(query) || 
             municipality.includes(query) || 
             type.includes(query) ||
             summary.includes(query);
    }
    
    return true;
  });

  // Calculate counts from real data
  const upcomingMeetingsCount = allMeetings.filter((meeting: any) => {
    return meeting.status === 'posted' || meeting.status === 'today' || (meeting.dateObj && isFuture(meeting.dateObj));
  }).length;
  
  const municipalitiesCount = uniqueMunicipalities.length;
  
  // Calculate week boundaries first (needed for filtering)
  const now = new Date();
  const thisWeekStart = startOfWeek(now, { weekStartsOn: 1 }); // Monday
  const thisWeekEnd = endOfWeek(now, { weekStartsOn: 1 }); // Sunday
  const nextWeekStart = addDays(thisWeekEnd, 1);
  const nextWeekEnd = addDays(nextWeekStart, 6);

  // Filter meetings by time filter (upcoming/past)
  const filteredMeetings = allMeetings.filter((meeting: any) => {
    if (!meeting.dateObj) return false;
    if (timeFilter === "upcoming") {
      return isFuture(meeting.dateObj) || isToday(meeting.dateObj);
    } else {
      return isPast(meeting.dateObj) && !isToday(meeting.dateObj);
    }
  });

  // Get meetings beyond this week and next week for "load more"
  const futureMeetings = filteredMeetings.filter((meeting: any) => {
    if (!meeting.dateObj) return false;
    if (timeFilter !== "upcoming") return false;
    // Exclude this week and next week meetings (they're shown separately)
    return !isWithinInterval(meeting.dateObj, { start: thisWeekStart, end: nextWeekEnd });
  });

  // Limit displayed future meetings
  const displayedFutureMeetings = futureMeetings.slice(0, displayLimit);
  const hasMoreFutureMeetings = futureMeetings.length > displayLimit;

  // Handle load more
  const handleLoadMore = () => {
    setDisplayLimit(prev => prev + 20); // Load 20 more meetings
  };

  // Reset display limit when time filter changes
  useEffect(() => {
    setDisplayLimit(20);
  }, [timeFilter]);
  
  // Split meetings by week
  
  const thisWeekMeetings = filteredMeetings.filter((meeting: any) => {
    if (!meeting.dateObj) return false;
    return isWithinInterval(meeting.dateObj, { start: thisWeekStart, end: thisWeekEnd });
  });
  
  const nextWeekMeetings = filteredMeetings.filter((meeting: any) => {
    if (!meeting.dateObj) return false;
    return isWithinInterval(meeting.dateObj, { start: nextWeekStart, end: nextWeekEnd });
  });

  const handleDayClick = (day: any) => {
    // Find meetings for this day (using filtered meetings)
    const year = currentCalendarDate.getFullYear();
    const month = currentCalendarDate.getMonth();
    
    let dayDate: Date;
    if (day.prev) {
      // Previous month
      const prevMonth = month === 0 ? 11 : month - 1;
      const prevYear = month === 0 ? year - 1 : year;
      dayDate = new Date(prevYear, prevMonth, day.day);
    } else if (day.next) {
      // Next month
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextYear = month === 11 ? year + 1 : year;
      dayDate = new Date(nextYear, nextMonth, day.day);
    } else {
      // Current month
      dayDate = new Date(year, month, day.day);
    }
    
    const dayMeetings = municipalityFilteredMeetings.filter((meeting: any) => {
      if (!meeting.dateObj) return false;
      const meetingDate = new Date(meeting.dateObj);
      return meetingDate.getDate() === day.day && 
             meetingDate.getMonth() === dayDate.getMonth() && 
             meetingDate.getFullYear() === dayDate.getFullYear();
    });
    
    setSelectedDate({
      dateString: format(dayDate, 'EEEE, MMMM d, yyyy'),
      meetings: dayMeetings
    });
    setViewSummaryMeeting(null);
  };

  const handleViewSummary = (meeting: any) => {
    setViewSummaryMeeting(meeting);
  };

  const handleModalClose = () => {
     setSelectedDate(null);
     setViewSummaryMeeting(null);
  };

  const handleBackToList = () => {
    if (selectedDate) {
      setViewSummaryMeeting(null);
    } else {
      handleModalClose();
    }
  };

  // Determine if dialog is open
  const isDialogOpen = !!selectedDate || !!viewSummaryMeeting;

  // If we are in summary view without a selected date (direct click from list), 
  // we still need to show the summary content.
  const activeMeeting = viewSummaryMeeting;

  // Generate calendar days dynamically based on currentCalendarDate
  const generateCalendarDays = () => {
    const year = currentCalendarDate.getFullYear();
    const month = currentCalendarDate.getMonth();
    
    // Get first day of the month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDay = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    // Get last day of previous month
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    
    const today = new Date();
    const isToday = (day: number, isCurrentMonth: boolean) => {
      return isCurrentMonth && 
             day === today.getDate() && 
             month === today.getMonth() && 
             year === today.getFullYear();
    };
    
    const days: Array<{ day: number; current: boolean; prev?: boolean; next?: boolean; isToday?: boolean }> = [];
    
    // Add previous month's trailing days
    for (let i = startDay - 1; i >= 0; i--) {
      days.push({ day: prevMonthLastDay - i, current: false, prev: true });
    }
    
    // Add current month's days
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({ day, current: true, isToday: isToday(day, true) });
    }
    
    // Add next month's leading days to fill the grid (35 or 42 cells)
    const totalCells = days.length;
    const remainingCells = Math.ceil(totalCells / 7) * 7 - totalCells;
    for (let day = 1; day <= remainingCells; day++) {
      days.push({ day, current: false, next: true });
    }
    
    return days;
  };
  
  const CALENDAR_DAYS = generateCalendarDays();
  
  // Navigation functions
  const goToPreviousMonth = () => {
    setCurrentCalendarDate(new Date(currentCalendarDate.getFullYear(), currentCalendarDate.getMonth() - 1, 1));
  };
  
  const goToNextMonth = () => {
    setCurrentCalendarDate(new Date(currentCalendarDate.getFullYear(), currentCalendarDate.getMonth() + 1, 1));
  };
  
  const goToToday = () => {
    setCurrentCalendarDate(new Date());
  };
  
  // Format month/year string
  const monthYearString = format(currentCalendarDate, 'MMMM yyyy');

    return (
      <>
        <div className="w-full">
          {/* Main Content */}
          <main className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
            <div className="p-8 max-w-[1600px] mx-auto space-y-8">
              
              {/* Header Section */}
              <div className="space-y-6">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
              <div>
                    <h1 className="text-3xl font-bold text-foreground tracking-tight">Municipal Meetings</h1>
                    <p className="text-neutral-500 mt-2 max-w-2xl leading-relaxed">
                      Stay informed with a consolidated schedule of council, committee, and public meetings across all your monitored municipalities.
                </p>
              </div>
                  
                  {/* Stats Cards Small */}
                  <div className="flex gap-4">
                    <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-3 rounded-lg shadow-sm flex items-center gap-3">
                       <div className="h-10 w-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center">
                         <CalendarDays className="h-5 w-5" />
                </div>
                       <div>
                         <div className="text-2xl font-bold leading-none">{isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : upcomingMeetingsCount}</div>
                         <div className="text-[10px] font-medium text-neutral-500 uppercase tracking-wider">Upcoming</div>
                        </div>
                        </div>
                    <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-3 rounded-lg shadow-sm flex items-center gap-3">
                       <div className="h-10 w-10 rounded-full bg-green-50 text-green-600 flex items-center justify-center">
                         <MapPin className="h-5 w-5" />
                        </div>
                       <div>
                         <div className="text-2xl font-bold leading-none">{isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : municipalitiesCount}</div>
                         <div className="text-[10px] font-medium text-neutral-500 uppercase tracking-wider">Municipalities</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Toolbar */}
                <div className="flex flex-col xl:flex-row items-center justify-between gap-4 bg-white dark:bg-neutral-900 p-2 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm">
                   <div className="flex items-center gap-2 w-full xl:w-auto">
                     <div className="relative flex-1 xl:w-96">
                       <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                       <Input 
                         placeholder="Search by committee, topic, or keyword..." 
                         className="pl-9 bg-neutral-50 dark:bg-neutral-950 border-transparent focus:bg-white dark:focus:bg-neutral-900 transition-all h-10"
                         value={searchQuery}
                         onChange={(e) => setSearchQuery(e.target.value)}
                       />
                     </div>
                     <Button 
                       variant="outline" 
                       className="h-10 gap-2 border-neutral-200 dark:border-neutral-800 text-neutral-600"
                       onClick={() => setFilterOpen(true)}
                     >
                       <Filter className="h-4 w-4" />
                       <span className="hidden sm:inline">Filters</span>
                       {selectedMunicipalities.length > 0 && (
                         <span className="ml-1 bg-primary text-primary-foreground text-xs px-1.5 py-0.5 rounded-full">
                           {selectedMunicipalities.length}
                         </span>
                       )}
                     </Button>
                   </div>

                   <div className="flex items-center gap-3 w-full xl:w-auto justify-between xl:justify-end">
                      
                      {/* Filter Toggle - Left side of the right group */}
                      <AnimatePresence>
                {viewMode === "list" && (
                          <motion.div 
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="flex items-center gap-3"
                          >
                            <div className="bg-neutral-50 dark:bg-neutral-800/50 p-1 rounded-lg flex items-center border border-neutral-100 dark:border-neutral-800">
                    <button 
                                  onClick={() => setTimeFilter("upcoming")}
                                  className={cn(
                                    "px-3 py-1 rounded-md text-xs font-medium transition-all",
                                    timeFilter === "upcoming" ? "bg-white dark:bg-neutral-700 text-foreground shadow-sm border border-neutral-200 dark:border-neutral-600" : "text-neutral-500 hover:text-neutral-900"
                                  )}
                    >
                      Upcoming
                    </button>
                    <button 
                                  onClick={() => setTimeFilter("past")}
                                  className={cn(
                                    "px-3 py-1 rounded-md text-xs font-medium transition-all",
                                    timeFilter === "past" ? "bg-white dark:bg-neutral-700 text-foreground shadow-sm border border-neutral-200 dark:border-neutral-600" : "text-neutral-500 hover:text-neutral-900"
                                  )}
                    >
                      Past
                    </button>
                  </div>
                            <div className="h-6 w-px bg-neutral-200 dark:bg-neutral-800 mx-1 hidden sm:block" />
                          </motion.div>
                )}
                      </AnimatePresence>

                      {/* View Toggle - Right side (Primary Position) */}
                      <div className="bg-neutral-100 dark:bg-neutral-800 p-1 rounded-lg flex items-center shrink-0">
                  <button 
                    onClick={() => setViewMode("list")}
                            className={cn(
                              "px-3 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-2",
                              viewMode === "list" ? "bg-white dark:bg-neutral-700 text-foreground shadow-sm" : "text-neutral-500 hover:text-neutral-900"
                            )}
                  >
                            <List className="h-4 w-4" />
                            List
                  </button>
                  <button 
                    onClick={() => setViewMode("calendar")}
                            className={cn(
                              "px-3 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-2",
                              viewMode === "calendar" ? "bg-white dark:bg-neutral-700 text-foreground shadow-sm" : "text-neutral-500 hover:text-neutral-900"
                            )}
                  >
                            <CalendarIcon className="h-4 w-4" />
                            Calendar
                  </button>
                      </div>

                   </div>
                </div>
              </div>

              {/* VIEW CONTENT */}
              {viewMode === "list" ? (
                <div className="space-y-8">
                  {/* This Week Section */}
                  {thisWeekMeetings.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-4 sticky top-0 bg-[#F4F5F7] dark:bg-neutral-950 py-2 z-10">
                         <div className="h-px flex-1 bg-neutral-200 dark:bg-neutral-800" />
                         <span className="text-xs font-bold text-neutral-400 uppercase tracking-wider bg-[#F4F5F7] dark:bg-neutral-950 px-2">This Week</span>
                         <div className="h-px flex-1 bg-neutral-200 dark:bg-neutral-800" />
                      </div>

                      <div className="grid grid-cols-1 gap-3">
                        {thisWeekMeetings.map((meeting: any, i: number) => (
                          <MeetingCard 
                            key={meeting.id} 
                            meeting={meeting} 
                            delay={i * 0.05} 
                            notified={notifiedMeetings.includes(meeting.id)}
                            onNotify={() => handleNotify(meeting.id)}
                            onViewSummary={() => handleViewSummary(meeting)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Next Week Section */}
                  {nextWeekMeetings.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-4 sticky top-0 bg-[#F4F5F7] dark:bg-neutral-950 py-2 z-10">
                         <div className="h-px flex-1 bg-neutral-200 dark:bg-neutral-800" />
                         <span className="text-xs font-bold text-neutral-400 uppercase tracking-wider bg-[#F4F5F7] dark:bg-neutral-950 px-2">Next Week</span>
                         <div className="h-px flex-1 bg-neutral-200 dark:border-neutral-800" />
                      </div>

                      <div className="grid grid-cols-1 gap-3">
                        {nextWeekMeetings.map((meeting: any, i: number) => (
                          <MeetingCard 
                            key={meeting.id} 
                            meeting={meeting} 
                            delay={i * 0.05} 
                            notified={notifiedMeetings.includes(meeting.id)}
                            onNotify={() => handleNotify(meeting.id)}
                            onViewSummary={() => handleViewSummary(meeting)}
                          />
                        ))}
            </div>
          </div>
                  )}

                  {/* Future Meetings Section (beyond next week) */}
                  {displayedFutureMeetings.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-4 sticky top-0 bg-[#F4F5F7] dark:bg-neutral-950 py-2 z-10">
                         <div className="h-px flex-1 bg-neutral-200 dark:bg-neutral-800" />
                         <span className="text-xs font-bold text-neutral-400 uppercase tracking-wider bg-[#F4F5F7] dark:bg-neutral-950 px-2">Future Meetings</span>
                         <div className="h-px flex-1 bg-neutral-200 dark:bg-neutral-800" />
                      </div>

                      <div className="grid grid-cols-1 gap-3">
                        {displayedFutureMeetings.map((meeting: any, i: number) => (
                          <MeetingCard 
                            key={meeting.id} 
                            meeting={meeting} 
                            delay={i * 0.05} 
                            notified={notifiedMeetings.includes(meeting.id)}
                            onNotify={() => handleNotify(meeting.id)}
                            onViewSummary={() => handleViewSummary(meeting)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {filteredMeetings.length === 0 && (
                    <div className="text-center py-12 text-neutral-500">
                      <p>No meetings found</p>
                    </div>
                  )}
                  
                  {/* Load More Trigger */}
                  {hasMoreFutureMeetings && (
                    <div className="py-8 text-center">
                      <Button 
                        variant="ghost" 
                        className="text-neutral-400 hover:text-neutral-600"
                        onClick={handleLoadMore}
                      >
                         Load more future meetings ({futureMeetings.length - displayLimit} remaining)
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                /* CALENDAR VIEW */
                (<div className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm p-6">
                  <div className="flex items-center justify-between mb-6">
                     <div className="flex items-center gap-4">
                        <h2 className="text-xl font-bold">{monthYearString}</h2>
                        <div className="flex items-center bg-neutral-100 dark:bg-neutral-800 rounded-lg p-0.5">
                           <Button 
                             variant="ghost" 
                             size="icon" 
                             className="h-7 w-7 rounded-md"
                             onClick={goToPreviousMonth}
                           >
                             <ChevronLeft className="h-4 w-4" />
                           </Button>
                           <Button 
                             variant="ghost" 
                             size="icon" 
                             className="h-7 w-7 rounded-md"
                             onClick={goToNextMonth}
                           >
                             <ChevronRight className="h-4 w-4" />
                           </Button>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="h-7 text-xs border-neutral-200"
                          onClick={goToToday}
                        >
                           Today
                        </Button>
                     </div>
                     
                     <Button 
                       variant="outline" 
                       size="sm" 
                       className="gap-2 text-neutral-600"
                       onClick={() => setColorCustomizationOpen(true)}
                     >
                        <Settings2 className="h-3.5 w-3.5" /> Customize Colors
                     </Button>
                 </div>
                  <div className="grid grid-cols-7 mb-2">
                     {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                       <div key={day} className="text-center text-xs font-semibold text-neutral-400 uppercase py-2">
                          {day}
                         </div>
                     ))}
                           </div>
                  <div className="grid grid-cols-7 grid-rows-5 gap-px bg-neutral-200 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden">
                     {CALENDAR_DAYS.map((day, i) => {
                       // Find meetings for this day (using filtered meetings)
                       const year = currentCalendarDate.getFullYear();
                       const month = currentCalendarDate.getMonth();
                       const dayMeetings = municipalityFilteredMeetings.filter((meeting: any) => {
                         if (!meeting.dateObj) return false;
                         const meetingDate = new Date(meeting.dateObj);
                         if (day.prev) {
                           // Previous month
                           const prevMonth = month === 0 ? 11 : month - 1;
                           const prevYear = month === 0 ? year - 1 : year;
                           return meetingDate.getDate() === day.day && 
                                  meetingDate.getMonth() === prevMonth && 
                                  meetingDate.getFullYear() === prevYear;
                         } else if (day.next) {
                           // Next month
                           const nextMonth = month === 11 ? 0 : month + 1;
                           const nextYear = month === 11 ? year + 1 : year;
                           return meetingDate.getDate() === day.day && 
                                  meetingDate.getMonth() === nextMonth && 
                                  meetingDate.getFullYear() === nextYear;
                         } else {
                           // Current month
                           return meetingDate.getDate() === day.day && 
                                  meetingDate.getMonth() === month && 
                                  meetingDate.getFullYear() === year;
                         }
                       });
                       
                       return (
                         <div 
                           key={i}
                           onClick={() => handleDayClick(day)}
                           className={cn(
                             "min-h-[120px] bg-white dark:bg-neutral-900 p-2 flex flex-col gap-1 transition-colors",
                             !day.current && "bg-neutral-50/50 dark:bg-neutral-950/50 text-neutral-400",
                             day.isToday && "ring-2 ring-inset ring-primary/20",
                             "cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800" // Make all days clickable
                           )}
                         >
                            <div className="flex justify-between items-start">
                               <span className={cn(
                                 "text-xs font-medium w-6 h-6 flex items-center justify-center rounded-full",
                                 day.isToday ? "bg-primary text-white" : "text-neutral-500"
                               )}>
                                 {day.day}
                               </span>
                            </div>
                            {dayMeetings.length > 0 && (
                              <div className="flex-1 flex flex-col gap-1 mt-1 overflow-y-auto">
                                {dayMeetings.slice(0, 3).map((meeting: any) => {
                                  const colorClass = meeting.color || 'bg-primary/10 text-primary';
                                  return (
                                    <div 
                                      key={meeting.id}
                                      className={cn(
                                        "text-[10px] px-1.5 py-0.5 rounded truncate cursor-pointer hover:opacity-80",
                                        colorClass,
                                        !day.current && "opacity-75" // Slightly dim meetings from other months
                                      )}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewSummary(meeting);
                                      }}
                                    >
                                      {meeting.title}
                                    </div>
                                  );
                                })}
                                {dayMeetings.length > 3 && (
                                  <div className={cn(
                                    "text-[10px] px-1.5",
                                    day.current ? "text-neutral-400" : "text-neutral-300"
                                  )}>
                                    +{dayMeetings.length - 3} more
                                  </div>
                                )}
                              </div>
                            )}
                         </div>
                       );
                           })}
                         </div>
                </div>)
              )}
            </div>
            )}
          </main>
        </div>
        {/* DAY / SUMMARY MODAL */}
        <Dialog open={isDialogOpen} onOpenChange={(open) => !open && handleModalClose()}>
          <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden bg-white border-neutral-200 text-neutral-900">
            
            {/* Header - Dynamic based on view */}
            {!activeMeeting ? (
              <div className="p-6 pb-4 border-b border-neutral-100">
                <DialogTitle className="text-lg font-semibold flex items-center justify-between">
                   <span>{selectedDate?.dateString}</span>
                </DialogTitle>
                <p className="text-sm text-neutral-500 mt-1">
                  {selectedDate?.meetings?.length || 0} meetings
                </p>
              </div>
            ) : (
              <div className="p-6 pb-4 border-b border-neutral-100">
                 {selectedDate && (
                   <Button 
                     variant="ghost" 
                     size="sm" 
                     className="h-6 px-2 -ml-2 mb-2 text-neutral-500 hover:text-neutral-900"
                     onClick={handleBackToList}
                   >
                     <ChevronLeft className="h-4 w-4 mr-1" /> Back to List
                   </Button>
                 )}
                 <DialogTitle className="text-xl font-bold">
                   {activeMeeting?.title}
                 </DialogTitle>
                 <div className="flex items-center gap-2 mt-1 text-sm text-neutral-500">
                   <span>{activeMeeting?.municipality}</span>
                   <span className="text-neutral-300">•</span>
                   <span>{activeMeeting?.formattedDate || activeMeeting?.date || selectedDate?.dateString}</span>
                        </div>
              </div>
            )}

            {/* Content */}
            <div className="p-6 bg-neutral-50/50 min-h-[300px]">
               <AnimatePresence mode="wait">
                 {!activeMeeting ? (
                   // LIST VIEW
                   (<motion.div 
                     key="list"
                     initial={{ opacity: 0, x: -20 }}
                     animate={{ opacity: 1, x: 0 }}
                     exit={{ opacity: 0, x: -20 }}
                     className="space-y-3"
                   >
                     {selectedDate?.meetings?.map((meeting: any) => (
                       <div key={meeting.id} className="bg-white p-4 rounded-lg border border-neutral-200 shadow-sm flex items-center justify-between group hover:border-neutral-300 transition-all">
                          <div className="flex-1 min-w-0">
                             <div className="flex items-start justify-between gap-4 mb-1">
                               <h3 className="font-semibold text-neutral-900 flex-1">{meeting.title}</h3>
                               <span className="text-xs font-medium text-neutral-400 whitespace-nowrap shrink-0">{meeting.formattedTime || meeting.time}</span>
                             </div>
                             <p className="text-sm text-neutral-500">{meeting.municipality}</p>
                      </div>
                      
                          <div className="flex items-center gap-2 ml-4">
                            {meeting.hasAgenda ? (
                               <>
                                 <Button 
                                   variant="ghost" 
                                   size="sm"
                                   className="text-neutral-400 hover:text-primary"
                                   onClick={() => handleViewSummary(meeting)}
                                 >
                                   <Sparkles className="h-4 w-4" />
                                 </Button>
                                 <Button 
                                   size="sm" 
                                   variant="outline" 
                                   className="h-8 gap-2"
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     if (meeting.documentUrl) {
                                       window.open(meeting.documentUrl, '_blank');
                                     }
                                   }}
                                   disabled={!meeting.documentUrl}
                                 >
                                   Agenda <ExternalLink className="h-3 w-3" />
                                 </Button>
                                  </>
                                ) : (
                               <Button 
                                 variant="ghost" 
                                 size="sm" 
                                 className={cn(
                                   "gap-2 text-xs font-medium transition-colors",
                                   notifiedMeetings.includes(meeting.id) ? "text-green-600 bg-green-50 hover:bg-green-100 hover:text-green-700" : "text-neutral-500 hover:text-neutral-900"
                                 )}
                                 onClick={() => handleNotify(meeting.id)}
                               >
                                 {notifiedMeetings.includes(meeting.id) ? (
                                   <>
                                     <Check className="h-3.5 w-3.5" /> Notifications On
                                  </>
                                ) : (
                                  <>
                                     <Bell className="h-3.5 w-3.5" /> Notify
                                  </>
                                )}
                               </Button>
                            )}
                          </div>
                       </div>
                     ))}
                     {(!selectedDate?.meetings || selectedDate.meetings.length === 0) && (
                       <div className="text-center py-12 text-neutral-400">
                         No meetings scheduled for this day.
                            </div>
                          )}
                   </motion.div>)
                 ) : (
                   // SUMMARY VIEW
                   (<motion.div 
                     key="summary"
                     initial={{ opacity: 0, x: 20 }}
                     animate={{ opacity: 1, x: 0 }}
                     exit={{ opacity: 0, x: 20 }}
                     className="space-y-6"
                   >
                     <div className="bg-white p-5 rounded-xl border border-neutral-200 shadow-sm space-y-4">
                        <div className="flex items-center gap-2 text-sm font-medium text-primary">
                          <Sparkles className="h-4 w-4" />
                          AI Agenda Analysis
                        </div>
                        <p className="text-neutral-600 leading-relaxed text-sm">
                          {activeMeeting?.summary || activeMeeting?.description || 'No summary available for this meeting.'}
                        </p>
                    </div>
                     <div className="flex justify-end gap-3">
                        <Button variant="outline" onClick={handleBackToList}>Close</Button>
                        <Button 
                          className="gap-2"
                          onClick={() => {
                            if (activeMeeting?.documentUrl) {
                              window.open(activeMeeting.documentUrl, '_blank');
                            }
                          }}
                          disabled={!activeMeeting?.documentUrl}
                        >
                          View Full Agenda <ExternalLink className="h-4 w-4" />
                        </Button>
                     </div>
                   </motion.div>)
                 )}
               </AnimatePresence>
                            </div>
          </DialogContent>
        </Dialog>
        {/* Municipality Filter Dialog */}
        <Dialog open={filterOpen} onOpenChange={setFilterOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Filter by Municipality</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-neutral-500">
                Select municipalities to filter meetings in the calendar view.
              </p>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                <div className="flex items-center gap-2 p-2 border rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-900 cursor-pointer">
                  <input
                    type="checkbox"
                    id="filter-all"
                    checked={selectedMunicipalities.length === 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedMunicipalities([]);
                      }
                    }}
                    className="rounded border-neutral-300"
                  />
                  <label htmlFor="filter-all" className="flex-1 cursor-pointer font-medium text-sm">
                    All Municipalities
                  </label>
                </div>
                {uniqueMunicipalities.map((municipality: string) => (
                  <div 
                    key={municipality}
                    className="flex items-center gap-2 p-2 border rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-900 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      id={`filter-${municipality}`}
                      checked={selectedMunicipalities.includes(municipality)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedMunicipalities([...selectedMunicipalities, municipality]);
                        } else {
                          setSelectedMunicipalities(selectedMunicipalities.filter(m => m !== municipality));
                        }
                      }}
                      className="rounded border-neutral-300"
                    />
                    <label 
                      htmlFor={`filter-${municipality}`} 
                      className="flex-1 cursor-pointer text-sm"
                    >
                      {municipality}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-between items-center gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setSelectedMunicipalities([]);
                }}
                disabled={selectedMunicipalities.length === 0}
              >
                Clear All
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setFilterOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={() => setFilterOpen(false)}>
                  Apply
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        {/* Color Customization Dialog */}
        <Dialog open={colorCustomizationOpen} onOpenChange={setColorCustomizationOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Customize Municipality Colors</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-neutral-500">
                Assign colors to different municipalities to make them easier to identify on the calendar.
              </p>
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {uniqueMunicipalitiesForColors.map((municipality: string) => {
                  const currentColor = municipalityColors[municipality] || '';
                  const [bgClass] = currentColor ? currentColor.split(' ') : ['bg-primary/10'];
                  
                  return (
                    <div key={municipality} className="flex items-center justify-between p-3 border border-neutral-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={cn("w-4 h-4 rounded", bgClass)} />
                        <span className="font-medium text-sm">{municipality}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1 flex-wrap max-w-[300px]">
                          {COLOR_OPTIONS.map((color) => {
                            const colorValue = `${color.bg} ${color.text}`;
                            const isSelected = currentColor === colorValue;
                            return (
                              <button
                                key={color.name}
                                onClick={() => {
                                  const newColors = { ...municipalityColors, [municipality]: colorValue };
                                  setMunicipalityColors(newColors);
                                  localStorage.setItem('municipalityColors', JSON.stringify(newColors));
                                }}
                                className={cn(
                                  "w-6 h-6 rounded border-2 transition-all",
                                  color.bg,
                                  isSelected ? "border-neutral-900 ring-2 ring-offset-1" : "border-transparent hover:border-neutral-300"
                                )}
                                title={color.name}
                              />
                            );
                          })}
                        </div>
                        {currentColor && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-neutral-500"
                            onClick={() => {
                              const newColors = { ...municipalityColors };
                              delete newColors[municipality];
                              setMunicipalityColors(newColors);
                              localStorage.setItem('municipalityColors', JSON.stringify(newColors));
                            }}
                          >
                            Reset
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setColorCustomizationOpen(false)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
}

// Helper Component for Meeting Card
function MeetingCard({ meeting, delay, notified, onNotify, onViewSummary }: any) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="bg-white dark:bg-neutral-900 p-4 rounded-lg border border-neutral-200 dark:border-neutral-800 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4 group hover:border-primary/30 transition-all"
    >
      <div className="flex items-start gap-4">
        <div className="flex flex-col items-center justify-center bg-neutral-50 dark:bg-neutral-800 rounded-lg w-14 h-14 shrink-0 border border-neutral-100 dark:border-neutral-700">
          <span className="text-[10px] font-bold text-neutral-400 uppercase">{meeting.dayName || 'N/A'}</span>
          <span className="text-lg font-bold text-neutral-900 dark:text-white">{meeting.dayNumber || ''}</span>
                          </div>
        
        <div>
          <div className="flex items-center gap-2 mb-1">
             <h3 className="font-bold text-neutral-900 dark:text-neutral-100">{meeting.title}</h3>
             {meeting.status === "cancelled" && (
               <Badge variant="destructive" className="text-[10px] h-5 px-1.5">Cancelled</Badge>
                        )}
                      </div>
          <div className="flex items-center gap-3 text-sm text-neutral-500">
             <span className="flex items-center gap-1">
               <Clock className="h-3.5 w-3.5" /> {meeting.formattedTime || meeting.time}
             </span>
             <span className="h-1 w-1 rounded-full bg-neutral-300" />
             <span>{meeting.municipality}</span>
             {meeting.items > 0 && (
               <>
                 <span className="h-1 w-1 rounded-full bg-neutral-300" />
                 <span className="text-neutral-400">{meeting.items} items</span>
               </>
          )}
        </div>
        </div>
      </div>

      <div className="flex items-center gap-2 sm:self-center self-end">
         {meeting.hasAgenda ? (
           <>
             <Button variant="outline" size="sm" className="gap-2 text-xs bg-white dark:bg-neutral-900" onClick={onViewSummary}>
               <Sparkles className="h-3.5 w-3.5 text-primary" /> Summary
             </Button>
             <Button 
               size="sm" 
               className="gap-2 text-xs shadow-sm"
               onClick={(e) => {
                 e.stopPropagation();
                 if (meeting.documentUrl) {
                   window.open(meeting.documentUrl, '_blank');
                 }
               }}
               disabled={!meeting.documentUrl}
             >
               Agenda <ExternalLink className="h-3 w-3 opacity-70" />
             </Button>
           </>
         ) : (
            <Button 
               variant="ghost" 
               size="sm" 
               className={cn(
                 "gap-2 text-xs font-medium transition-colors",
                 notified ? "text-green-600 bg-green-50 hover:bg-green-100 hover:text-green-700" : "text-neutral-500 hover:text-neutral-900"
               )}
               onClick={onNotify}
             >
               {notified ? (
                 <>
                   <Check className="h-3.5 w-3.5" /> Notifications On
                 </>
               ) : (
                 <>
                   <Bell className="h-3.5 w-3.5" /> Notify
                 </>
               )}
             </Button>
      )}
    </div>
    </motion.div>
  );
}